


import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../redux/store";
import { useComparativeData } from "../hooks/useComparativeData";
import { setActiveReportKey } from "../../../redux/comparativeDashSlice";

import { RotateCw, TrendingUp, TrendingDown } from "lucide-react";

interface Props {
  config: any;
  yr1Label: string;
  yr2Label: string;
  tableRef: React.RefObject<HTMLDivElement | null>;
  autoFetch: boolean;
  cumulativeMode?: number;
}

const ComparativeCard: React.FC<Props> = ({
  config,
   yr1Label,
  yr2Label,
  tableRef,
  autoFetch,
  cumulativeMode = 0,
}) => {
  const dispatch = useDispatch();

  // =====================================================
  // API HOOK
  // =====================================================

  const { fetchData, loading } = useComparativeData({
    reportKey: config.reportKey,
    endpoint: config.endpoint,
    extraParams: { ...config.extraParams, Cumulative: cumulativeMode },
  });

  // =====================================================
  // REDUX DATA
  // =====================================================

  const reportObj = useSelector(
    (state: RootState) =>
      state.comparativeDash.reportData[config.reportKey]
  );

  const cardData = reportObj?.DataY;

  const activeReportKey = useSelector(
    (state: RootState) => state.comparativeDash.activeReportKey
  );

  const isActive = activeReportKey === config.reportKey;

  // =====================================================

  // AUTO FETCH
  // =====================================================

  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [autoFetch, config.reportKey]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const onCardClick = async () => {
    dispatch(setActiveReportKey(config.reportKey));
    await fetchData();
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await fetchData();
  };

  // =====================================================
  // COMPUTED
  // =====================================================

  const growthVal = cardData?.GrowthPer ?? 0;
  const isPositive = growthVal >= 0;

  const formatAmount = (val: number) =>
    val
      ? val.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      <style>{`
        .comp-card-${config.reportKey} {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .comp-card-${config.reportKey}:hover {
          transform: translateY(-3px) !important;
          box-shadow: 0 12px 32px rgba(0,0,0,0.35) !important;
        }
        .comp-card-${config.reportKey}.active-card {
          box-shadow: 0 0 0 2px rgba(255,255,255,0.45), 0 8px 24px rgba(0,0,0,0.3) !important;
        }
        .refresh-btn-${config.reportKey} {
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .refresh-btn-${config.reportKey}:hover {
          background: rgba(255,255,255,0.22) !important;
          transform: rotate(30deg);
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .spin-icon { animation: spin 0.9s linear infinite; }

        @media (max-width: 575px) {
  .comp-data-row-${config.reportKey} {
    display: flex !important;
    flex-direction: column !important;
  }
  .comp-data-col-${config.reportKey} {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    border-right: none !important;
    padding: 10px 0 !important;
    border-bottom: 1px solid rgba(255,255,255,0.12) !important;
  }
  .comp-data-col-${config.reportKey}:last-child {
    border-bottom: none !important;
    padding-bottom: 0 !important;
  }
}
      `}</style>

      <div
        className={`comp-card-${config.reportKey} ${isActive ? "active-card" : ""}`}
        style={{
          backgroundColor: config.backgroundColor,
          borderRadius: "18px",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          transform: isActive ? "translateY(-3px) scale(1.01)" : "scale(1)",
          boxShadow: isActive
            ? "0 0 0 2px rgba(255,255,255,0.45), 0 8px 24px rgba(0,0,0,0.3)"
            : "0 4px 18px rgba(0,0,0,0.22)",
        }}
        onClick={onCardClick}
      >

        {/* ── subtle top shine ── */}
        <div
          style={{
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: "2px",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
          }}
        />

        {/* ── subtle radial glow top-right ── */}
        <div
          style={{
            position: "absolute",
            top: "-40px", right: "-40px",
            width: "160px", height: "160px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        {/* ===================================== */}
        {/* REFRESH BUTTON                        */}
        {/* ===================================== */}
        <button
          onClick={handleRefresh}
          className={`refresh-btn-${config.reportKey}`}
          style={{
            position: "absolute",
            top: "14px", right: "14px",
            width: "34px", height: "34px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.25)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 10,
          }}
        >
          <RotateCw
            size={15}
            className={loading ? "spin-icon" : ""}
          />
        </button>

        {/* ===================================== */}
        {/* CARD BODY                             */}
        {/* ===================================== */}
        <div style={{ padding: "22px 22px 20px" }} >

          {/* TITLE ROW */}
          <div style={{ marginBottom: "18px", paddingRight: "40px" }} >
            <h5
              style={{
                margin: "0 0 4px",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "15px",
                letterSpacing: "0.4px",
              }}
            >
              {config.title}
            </h5>
            <p
              style={{
                margin: 0,
                color: "rgba(255,255,255,0.6)",
                fontSize: "12px",
              }}
            >
              {config.subtitle}
            </p>
          </div>

          {/* DIVIDER */}
          <div
            style={{
              height: "1px",
              background: "rgba(255,255,255,0.12)",
              marginBottom: "18px",
            }}
          />

          {/* DATA ROW */}

          <div
          className={`comp-data-row-${config.reportKey}`}  
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0" }}
        >

            {/* ── LY ── */}

  <div
    className={`comp-data-col-${config.reportKey}`}  
    style={{ borderRight: "1px solid rgba(255,255,255,0.12)", paddingRight: "16px", display: "flex", flexDirection: "column", gap: "6px" }}
  >
  <span
    style={{
      color: "rgba(255,255,255,0.6)",
      fontSize: "11px",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      textAlign: "left",
    }}
  >
    FY {yr1Label}
    {/* FY {lyFullLabel} */}
  </span>

  <span
    style={{
      color: "#ffffff",
      fontWeight: 700,
      fontSize: "15px",
      lineHeight: 1.2,
      textAlign: "right",
      display: "block",
    }}
  >
    {loading && !cardData ? (
      <span style={{ opacity: 0.5, fontSize: "13px" }}>—</span>
    ) : (
      formatAmount(cardData?.LYAmount ?? 0)
    )}
  </span>
</div>

            {/* ── CY ── */}
       
  <div
    className={`comp-data-col-${config.reportKey}`}
    style={{ borderRight: "1px solid rgba(255,255,255,0.12)", padding: "0 16px", display: "flex", flexDirection: "column", gap: "6px" }}
  >

  <span
    style={{
      color: "rgba(255,255,255,0.6)",
      fontSize: "11px",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      textAlign: "left",
    }}
  >
    {/* FY {cyFullLabel} */}
    FY {yr2Label}
  </span>

  <span
    style={{
      color: "#ffffff",
      fontWeight: 700,
      fontSize: "15px",
      lineHeight: 1.2,
      textAlign: "right",
      display: "block",
    }}
  >
    {loading && !cardData ? (
      <span style={{ opacity: 0.5, fontSize: "13px" }}>—</span>
    ) : (
      formatAmount(cardData?.CYAmount ?? 0)
    )}
  </span>
</div>

            {/* ── GROWTH ── */}
         
  <div
    className={`comp-data-col-${config.reportKey}`} 
    style={{ paddingLeft: "16px", display: "flex", flexDirection: "column", gap: "6px" }}
  >
  <span
    style={{
      color: "rgba(255,255,255,0.6)",
      fontSize: "11px",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      textAlign: "left",
    }}
  >
    Growth %
  </span>

<div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "5px",
    width: "100%",
  }}
>
    {/* trend icon */}
    <div
      style={{
        width: "24px",
        height: "24px",
        borderRadius: "6px",
        background: isPositive
          ? "rgba(74,222,128,0.18)"
          : "rgba(255,107,107,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {isPositive ? (
        <TrendingUp size={13} color="#4ade80" />
      ) : (
        <TrendingDown size={13} color="#ff6b6b" />
      )}
    </div>

    <span
      style={{
        color: isPositive ? "#4ade80" : "#ff6b6b",
        fontWeight: 700,
        fontSize: "15px",
        lineHeight: 1.2,
      }}
    >
      {growthVal !== 0
        ? `${Math.abs(growthVal).toFixed(2)}%`
        : "0.00%"}
    </span>
  </div>
</div>
            
          </div>

          {/* LOADING OVERLAY HINT */}
          {loading && cardData && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <div
                style={{
                  width: "6px", height: "6px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.5)",
                  animation: "spin 1s linear infinite",
                }}
              />
              <span
                style={{
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.45)",
                }}
              >
                Refreshing...
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ComparativeCard;